import { Router } from '@angular/router';

// Shared across the two layouts, which are destroyed/recreated during navigation.
let switching = false;

function waitForMenu(mode: 'user' | 'admin'): Promise<void> {
    return new Promise(resolve => {
        const selector = `.gsdc-sidebar[data-portal-mode="${mode}"][data-mode-ready="true"]`;
        let timer: ReturnType<typeof setTimeout>;
        const finish = () => { observer.disconnect(); clearTimeout(timer); resolve(); };
        const observer = new MutationObserver(() => { if (document.querySelector(selector)) finish(); });
        observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-mode-ready'] });
        // A slow or failed menu request must never block navigation indefinitely.
        timer = setTimeout(finish, 700);
        if (document.querySelector(selector)) finish();
    });
}

export async function switchPortalMode(event: MouseEvent, router: Router, path: string): Promise<void> {
    // Preserve native links: open in a new tab/window, copy address and keyboard activation.
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (switching) return;
    switching = true;
    const root = document.documentElement;
    const mode = path.startsWith('/admin') ? 'admin' : 'user';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let navigation: Promise<boolean> | undefined;
    const navigate = () => navigation ||= (async () => {
        const moved = await router.navigateByUrl(path);
        if (moved && !reducedMotion) await waitForMenu(mode);
        return moved;
    })();
    try {
        const start = (document as any).startViewTransition;
        if (!reducedMotion && typeof start === 'function') {
            root.dataset.portalDirection = mode;
            root.classList.add('portal-mode-transition');
            try {
                const transition = start.call(document, async () => { await navigate(); });
                transition.ready.catch(() => {});
                transition.updateCallbackDone.catch(() => {});
                await transition.finished;
            } catch (_) {
                // Rendering/animation failures must not navigate a second time.
                await navigate();
            }
        } else {
            const moved = await navigate();
            if (moved && !reducedMotion) {
                document.querySelector('.portal-page-content')?.animate?.(
                    [{ transform: 'translateY(4px)' }, { transform: 'none' }],
                    { duration: 180, easing: 'ease-out' });
            }
        }
    } finally {
        root.classList.remove('portal-mode-transition');
        delete root.dataset.portalDirection;
        switching = false;
    }
}
